import React, { useState, useEffect } from 'react';
import { EmployeeCashManager } from '../utils/employeeCashManager';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { useNotification } from '../contexts/NotificationContext';
import { 
  DollarSign, 
  ArrowDown, 
  ArrowUp, 
  Wallet, 
  Smartphone,
  FileText,
  CheckCircle,
  XCircle,
  Filter,
  Search
} from 'lucide-react';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';

const EmployeeCashFlow = ({ employeeId, employeeName }) => {
  const { currentCompany } = useEnhancedCompany();
  const { success, error: notifyError, warning } = useNotification();
  
  const [paymentMode, setPaymentMode] = useState('cash'); // 'cash' or 'upi'
  const [transactionType, setTransactionType] = useState('inflow');
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    category: 'General',
    referenceNumber: '',
    // Cash fields
    cashReceivedFrom: '',
    cashGivenTo: '',
    cashCountedBy: '',
    cashVerified: false,
    // UPI fields
    upiId: '',
    transactionId: '',
    upiApp: 'PhonePe',
    referenceNumber: '',
    screenshotUrl: ''
  });
  
  const [balance, setBalance] = useState({ total: 0, cash: 0, upi: 0 });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    paymentMode: '',
    transactionType: '',
    startDate: '',
    endDate: ''
  });
  
  // Load balance and transactions
  useEffect(() => {
    if (employeeId && currentCompany?.companyId) {
      loadEmployeeData();
    }
  }, [employeeId, currentCompany?.companyId]);
  
  const loadEmployeeData = async () => {
    if (!currentCompany?.companyId || !employeeId) return;
    
    setLoading(true);
    const companyData = {
      companyId: currentCompany.companyId,
      userId: currentCompany.userId,
      appId: currentCompany.appId
    };
    
    try {
      // Load balance
      const balanceResult = await EmployeeCashManager.getEmployeeBalance(companyData, employeeId);
      if (balanceResult) {
        setBalance(balanceResult);
      }
      
      // Load transactions
      const ledgerResult = await EmployeeCashManager.getEmployeeCashLedger(companyData, employeeId, filters);
      if (ledgerResult.success) {
        setTransactions(ledgerResult.data);
      }
    } catch (error) {
      console.error('Error loading employee data:', error);
      notifyError('Failed to load employee cash data');
    } finally {
      setLoading(false);
    }
  };
  
  // Reload when filters change
  useEffect(() => {
    if (employeeId && currentCompany?.companyId) {
      loadEmployeeData();
    }
  }, [filters]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentCompany?.companyId || !employeeId) {
      warning('Company or employee information missing');
      return;
    }
    
    // Validate amount
    const amountNum = parseFloat(formData.amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      warning('Please enter a valid amount');
      return;
    }
    
    // Validate payment mode specific fields
    if (paymentMode === 'cash') {
      if (transactionType === 'inflow' && !formData.cashReceivedFrom) {
        warning('Please enter who gave the cash');
        return;
      }
      if (transactionType === 'outflow' && !formData.cashGivenTo) {
        warning('Please enter who received the cash');
        return;
      }
    } else if (paymentMode === 'upi') {
      if (!formData.upiId) {
        warning('Please enter UPI ID');
        return;
      }
      if (!formData.transactionId) {
        warning('Please enter UPI Transaction ID');
        return;
      }
      
      // Validate UPI ID format
      const upiValidation = EmployeeCashManager.validateUpiId(formData.upiId);
      if (!upiValidation.valid) {
        warning(upiValidation.error);
        return;
      }
    }
    
    const companyData = {
      companyId: currentCompany.companyId,
      userId: currentCompany.userId,
      appId: currentCompany.appId
    };
    
    const transactionData = {
      employeeId,
      employeeName: employeeName || '',
      transactionType,
      amount: amountNum,
      paymentMode,
      category: formData.category,
      description: formData.description,
      referenceNumber: formData.referenceNumber,
      createdBy: {
        userId: currentCompany.userId,
        name: currentCompany.userName || 'User'
      },
      ...(paymentMode === 'cash' && {
        cashReceivedFrom: formData.cashReceivedFrom,
        cashGivenTo: formData.cashGivenTo,
        cashCountedBy: formData.cashCountedBy,
        cashVerified: formData.cashVerified
      }),
      ...(paymentMode === 'upi' && {
        upiId: formData.upiId,
        transactionId: formData.transactionId,
        upiApp: formData.upiApp,
        referenceNumber: formData.referenceNumber,
        screenshotUrl: formData.screenshotUrl
      })
    };
    
    const result = await EmployeeCashManager.createCashTransaction(companyData, transactionData);
    
    if (result.success) {
      success('Transaction recorded successfully!');
      loadEmployeeData();
      // Reset form
      setFormData({
        amount: '',
        description: '',
        category: 'General',
        referenceNumber: '',
        cashReceivedFrom: '',
        cashGivenTo: '',
        cashCountedBy: '',
        cashVerified: false,
        upiId: '',
        transactionId: '',
        upiApp: 'PhonePe',
        referenceNumber: '',
        screenshotUrl: ''
      });
    } else {
      notifyError(result.error || 'Failed to record transaction');
    }
  };
  
  const formatDate = (date) => {
    if (!date) return '-';
    if (date.toDate) {
      return date.toDate().toLocaleDateString('en-IN');
    }
    if (date.seconds) {
      return new Date(date.seconds * 1000).toLocaleDateString('en-IN');
    }
    return new Date(date).toLocaleDateString('en-IN');
  };
  
  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN')}`;
  };
  
  if (loading && transactions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading cash flow data...</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Balance</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(balance.total)}</p>
            </div>
            <Wallet className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Cash Balance</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(balance.cash)}</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-600" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">UPI Balance</p>
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(balance.upi)}</p>
            </div>
            <Smartphone className="w-8 h-8 text-purple-600" />
          </div>
        </div>
      </div>
      
      {/* Transaction Form */}
      <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200 shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Record Transaction</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Transaction Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Transaction Type</label>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="inflow"
                  checked={transactionType === 'inflow'}
                  onChange={(e) => setTransactionType(e.target.value)}
                  className="mr-2"
                />
                <ArrowDown className="w-4 h-4 text-green-600 mr-1" />
                <span>Money Received (Inflow)</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="outflow"
                  checked={transactionType === 'outflow'}
                  onChange={(e) => setTransactionType(e.target.value)}
                  className="mr-2"
                />
                <ArrowUp className="w-4 h-4 text-red-600 mr-1" />
                <span>Money Spent (Outflow)</span>
              </label>
            </div>
          </div>
          
          {/* Payment Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode</label>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="cash"
                  checked={paymentMode === 'cash'}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  className="mr-2"
                />
                <DollarSign className="w-4 h-4 text-green-600 mr-1" />
                <span>Cash</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="upi"
                  checked={paymentMode === 'upi'}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  className="mr-2"
                />
                <Smartphone className="w-4 h-4 text-purple-600 mr-1" />
                <span>UPI</span>
              </label>
            </div>
          </div>
          
          {/* Common Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Amount *</label>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0.00"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <AppSelect
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                aria-label="Category"
                sheetTitle="Category"
                options={[
                  { value: 'General', label: 'General' },
                  { value: 'Petty Cash', label: 'Petty Cash' },
                  { value: 'Collection', label: 'Collection' },
                  { value: 'Expense', label: 'Expense' },
                  { value: 'Advance', label: 'Advance' },
                  { value: 'Refund', label: 'Refund' },
                  { value: 'Salary', label: 'Salary' },
                ]}
                triggerClassName={APP_SELECT_FILTER_TRIGGER}
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter transaction description"
            />
          </div>
          
          {/* Cash-Specific Fields */}
          {paymentMode === 'cash' && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-medium text-green-900 mb-3">Cash Payment Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {transactionType === 'inflow' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cash Received From *
                    </label>
                    <input
                      type="text"
                      value={formData.cashReceivedFrom}
                      onChange={(e) => setFormData({...formData, cashReceivedFrom: e.target.value})}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Name of person/party"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cash Given To *
                    </label>
                    <input
                      type="text"
                      value={formData.cashGivenTo}
                      onChange={(e) => setFormData({...formData, cashGivenTo: e.target.value})}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Name of person/party"
                    />
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cash Counted/Verified By
                  </label>
                  <input
                    type="text"
                    value={formData.cashCountedBy}
                    onChange={(e) => setFormData({...formData, cashCountedBy: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Name of verifier"
                  />
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.cashVerified}
                    onChange={(e) => setFormData({...formData, cashVerified: e.target.checked})}
                    className="mr-2"
                  />
                  <label className="text-sm text-gray-700">Cash Verified</label>
                </div>
              </div>
            </div>
          )}
          
          {/* UPI-Specific Fields */}
          {paymentMode === 'upi' && (
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-medium text-purple-900 mb-3">UPI Payment Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    UPI ID *
                  </label>
                  <input
                    type="text"
                    value={formData.upiId}
                    onChange={(e) => setFormData({...formData, upiId: e.target.value})}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="employee@paytm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    UPI App
                  </label>
                  <AppSelect
                    value={formData.upiApp}
                    onChange={(e) => setFormData({...formData, upiApp: e.target.value})}
                    aria-label="UPI App"
                    sheetTitle="UPI App"
                    options={[
                      { value: 'PhonePe', label: 'PhonePe' },
                      { value: 'GooglePay', label: 'Google Pay' },
                      { value: 'Paytm', label: 'Paytm' },
                      { value: 'BHIM', label: 'BHIM' },
                      { value: 'AmazonPay', label: 'Amazon Pay' },
                      { value: 'Other', label: 'Other' },
                    ]}
                    triggerClassName={APP_SELECT_FILTER_TRIGGER}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    UPI Transaction ID *
                  </label>
                  <input
                    type="text"
                    value={formData.transactionId}
                    onChange={(e) => setFormData({...formData, transactionId: e.target.value})}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="UPI transaction reference"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reference Number
                  </label>
                  <input
                    type="text"
                    value={formData.referenceNumber}
                    onChange={(e) => setFormData({...formData, referenceNumber: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional reference"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Screenshot (Optional)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // TODO: Upload to Firebase Storage and get URL
                        // For now, just store filename
                        setFormData({...formData, screenshotUrl: file.name});
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Reference Number</label>
            <input
              type="text"
              value={formData.referenceNumber}
              onChange={(e) => setFormData({...formData, referenceNumber: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Optional reference number"
            />
          </div>
          
          <button
            type="submit"
            className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors font-medium"
          >
            Record Transaction
          </button>
        </form>
      </div>
      
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
        <div className="flex items-center mb-4">
          <Filter className="w-5 h-5 text-gray-600 mr-2" />
          <h3 className="text-lg font-semibold">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode</label>
            <AppSelect
              value={filters.paymentMode}
              onChange={(e) => setFilters({...filters, paymentMode: e.target.value})}
              aria-label="Payment Mode"
              sheetTitle="Payment Mode"
              placeholder="All Modes"
              options={[
                { value: '', label: 'All Modes' },
                { value: 'cash', label: 'Cash' },
                { value: 'upi', label: 'UPI' },
              ]}
              triggerClassName={APP_SELECT_FILTER_TRIGGER}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Transaction Type</label>
            <AppSelect
              value={filters.transactionType}
              onChange={(e) => setFilters({...filters, transactionType: e.target.value})}
              aria-label="Transaction Type"
              sheetTitle="Transaction Type"
              placeholder="All Types"
              options={[
                { value: '', label: 'All Types' },
                { value: 'inflow', label: 'Inflow' },
                { value: 'outflow', label: 'Outflow' },
              ]}
              triggerClassName={APP_SELECT_FILTER_TRIGGER}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({...filters, startDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({...filters, endDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
      
      {/* Transaction History */}
      <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Transaction History</h3>
          <div className="text-sm text-gray-600">
            Total: {transactions.length} transactions
          </div>
        </div>
        
        {transactions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p>No transactions found</p>
          </div>
        ) : (
          <>
          <div className="md:hidden space-y-2 mb-4">
            {transactions.map((transaction) => (
              <div key={transaction.id} className="border border-gray-200 rounded-lg p-3 bg-white">
                <div className="flex justify-between items-start gap-2 mb-2">
                  <span className="text-xs text-gray-500">{formatDate(transaction.date)}</span>
                  <div className="flex gap-1.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      transaction.transactionType === 'inflow' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>{transaction.transactionType === 'inflow' ? 'Inflow' : 'Outflow'}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      transaction.paymentMode === 'cash' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'
                    }`}>{transaction.paymentMode === 'cash' ? 'Cash' : 'UPI'}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center text-sm mb-1">
                  <span className="font-semibold text-gray-900">{formatCurrency(transaction.amount)}</span>
                  <span className="text-gray-600 text-xs">Bal: {formatCurrency(transaction.balance)}</span>
                </div>
                {transaction.description && (
                  <p className="text-xs text-gray-600 line-clamp-2">{transaction.description}</p>
                )}
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mode</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" title="Balance after this transaction (chronological order)">Balance (after tx)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                      {formatDate(transaction.date)}
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        transaction.transactionType === 'inflow' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {transaction.transactionType === 'inflow' ? 'Inflow' : 'Outflow'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        transaction.paymentMode === 'cash' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {transaction.paymentMode === 'cash' ? (
                          <span className="flex items-center">
                            <DollarSign className="w-3 h-3 inline mr-1" />
                            Cash
                          </span>
                        ) : (
                          <span className="flex items-center">
                            <Smartphone className="w-3 h-3 inline mr-1" />
                            UPI
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                      {formatCurrency(transaction.amount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {formatCurrency(transaction.balance)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {transaction.description || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      {transaction.status === 'approved' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : transaction.status === 'rejected' ? (
                        <XCircle className="w-5 h-5 text-red-600" />
                      ) : (
                        <span className="text-yellow-600 text-xs">Pending</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeeCashFlow;

